import React from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Heart, Activity, Brain, Target, Calendar, ChevronRight } from 'lucide-react';
import { ClientMetrics } from './ClientMetrics';
import { ClientWorkoutHistory } from './ClientWorkoutHistory';
import { ClientGoals } from './ClientGoals';
import { ClientNotes } from './ClientNotes';
import { ClientSchedule } from './ClientSchedule';

interface ClientProfileProps {
  client: {
    id: string;
    name: string;
    image: string;
    program: string;
    progress: number;
    lastActive: string;
    metrics: {
      recovery: number;
      strain: number;
      sleep: number;
    };
  };
  onBack: () => void;
}

export function ClientProfile({ client, onBack }: ClientProfileProps) {
  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronLeft className="w-5 h-5" />
        Back to Clients
      </button>

      {/* Client Header */}
      <div className="theme-card p-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-50" />
        <div className="relative flex items-center gap-6">
          <img
            src={client.image}
            alt={client.name}
            className="w-24 h-24 rounded-xl object-cover"
          />
          <div>
            <h1 className="text-2xl font-bold">{client.name}</h1>
            <p className="text-muted-foreground">{client.program}</p>
            <div className="flex items-center gap-4 mt-2">
              <div className="flex items-center gap-1 text-sm">
                <Target className="w-4 h-4 text-primary" />
                <span>{client.progress}% Progress</span>
              </div>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Calendar className="w-4 h-4" />
                <span>Last active {client.lastActive}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button className="theme-card p-4 flex items-center justify-between group hover:bg-primary/10 transition-colors">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-primary" />
            <span>Schedule Session</span>
          </div>
          <ChevronRight className="w-5 h-5 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
        <button className="theme-card p-4 flex items-center justify-between group hover:bg-primary/10 transition-colors">
          <div className="flex items-center gap-3">
            <Target className="w-5 h-5 text-primary" />
            <span>Update Goals</span>
          </div>
          <ChevronRight className="w-5 h-5 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
        <button className="theme-card p-4 flex items-center justify-between group hover:bg-primary/10 transition-colors">
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-primary" />
            <span>Create Workout</span>
          </div>
          <ChevronRight className="w-5 h-5 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
      </div>

      {/* Client Metrics */}
      <ClientMetrics metrics={client.metrics} />

      {/* Client Goals */}
      <ClientGoals clientId={client.id} />

      {/* Workout History */}
      <ClientWorkoutHistory clientId={client.id} />

      {/* Schedule */}
      <ClientSchedule clientId={client.id} />

      {/* Notes */}
      <ClientNotes clientId={client.id} />
    </div>
  );
}